import { NextPage, NextPageContext } from 'next';
import Script from 'next/script';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { addFilters, applyFilters, getRentals, setLocation } from '@/actions/rentals';
import { CampgroundPage as CampgroundPageContent } from '@/components/route/campground/CampgroundPage';
import { ICampgroundProps } from '@/components/route/campground/campgroundTypes';
import defaultImages from '@/components/route/campground/defaultCampgroundImages';
import { setIsLoading } from '@/redux/modules/listing';
import { trackCampgroundPageViewed } from '@/services/analytics/campground';
import { EListingSource } from '@/services/analytics/listings/types';
import getPageData from '@/services/getPageData';
import { trackEvent } from '@/services/track-event';
import { getReferrer } from '@/utility/getReferrer';
import { IStore } from '@/utility/redux/store';

const INITIAL_RENTALS_LENGTH = 8;

const CampgroundPage: NextPage<ICampgroundProps> = props => {
  const dispatch = useDispatch();

  useEffect(() => {
    trackEvent({
      event: 'Locality : Viewed page',
      pageType: 'campground pdp',
    });
  }, []);

  useEffect(() => {
    trackCampgroundPageViewed({
      isClaimed: false,
      isFromSignaturePage: false,
      previousPageUrl: window.sessionStorage?.getItem('previousPageUrl') || getReferrer() || null,
      campgroundID: Number(props.id),
      campgroundName: props.metadata.title,
      subcategoryReviews: null,
      reviewsCount: null,
      reviewsAverage: null,
      numberOfCategories: null,
      numberOfSites: null,
      campgroundCity: props.metadata.campground_info?.address.city || '',
      campgroundState: props.metadata.campground_info?.address.state || '',
      departureDate: null,
      returnDate: null,
      totalNights: null,
      source: EListingSource.CAMPGROUND,
      flexibleDateRange: null,
      isOutdoorsyDestination: false,
      odnID: '',
      campgroundCheckInTime: '',
      campgroundCheckOutTime: '',
      isCampground: false,
      campgroundAddress: '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(setIsLoading(false));

    return () => {
      dispatch(addFilters({ sort: null }));
    };
  }, [dispatch]);

  return (
    <>
      <Script src={process.env.convertFlowScript} strategy="beforeInteractive" />
      <CampgroundPageContent {...props} />
    </>
  );
};

CampgroundPage.getInitialProps = async ({
  req,
  res,
  query,
  reduxStore,
}: NextPageContext & { req: { path: string }; reduxStore: IStore }) => {
  let error = null;
  let pageData;

  if (!query.locale) {
    query.locale = 'en-us';
  }
  if (query.url_path && typeof query.url_path === 'string' && !query.url_path.startsWith('/')) {
    query.url_path = `/${query.url_path}`;
  }

  try {
    pageData = await getPageData({ req, res, query });
  } catch {
    error = {
      statusCode: 500,
      message: 'Unable to fetch page data. Please try again.',
    };
  }

  const campground_info = pageData?.metadata?.campground_info || {};
  let images = campground_info ? pageData?.metadata?.campground_info?.media : defaultImages;
  if (!images?.length) {
    images = defaultImages;
  }

  if (images.length < 3) {
    const maxLength = images.length + 1;
    images = images.concat(defaultImages).slice(0, maxLength);
  }

  const { location } = pageData?.metadata || {};

  reduxStore.dispatch(addFilters({ sort: '-num_reviews' }));
  reduxStore.dispatch(applyFilters());
  reduxStore.dispatch(setLocation(location?.lat, location?.lon));
  await reduxStore.dispatch(getRentals({ count: INITIAL_RENTALS_LENGTH }));

  return {
    ...(pageData || {}),
    images,
    error,
  };
};

export default CampgroundPage;
